import { TextField, Button } from "@mui/material";
import FormFieldErrorClass from "./models/formFieldError";
import ErrorIcon from "@mui/icons-material/Error";
import { useState, useEffect } from "react";
import isPostalCode from "validator/lib/isPostalCode";
import {
  InternationalPhoneInput,
  isPhoneNumberValid,
} from "../PhoneNumberInput";
import {
  FormContainer,
  FormSubmitError,
  ActionButtonsContainer,
  HeaderNote,
} from "./shared";
import { states, provinces, countries } from "./shared/states";

const OrganizationLocationForm = ({
  handleCancel = () => {},
  handleSave = () => {},
  loading = false,
  error = "",
  location,
  isEdit = false,
}) => {
  const [isLocationLoaded, setIsLocationLoaded] = useState(false);

  const [statesList, setStatesList] = useState([]);
  const [stateLabel, setStateLabel] = useState("State");
  const [zipLabel, setZipLabel] = useState("Zip Code");

  const [save, setSave] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [countryCode, setCountryCode] = useState("US");

  const [nameError, setNameError] = useState(new FormFieldErrorClass());
  const [phoneError, setPhoneError] = useState(new FormFieldErrorClass());
  const [address1Error, setAddress1Error] = useState(new FormFieldErrorClass());
  const [address2Error, setAddress2Error] = useState(new FormFieldErrorClass());
  const [cityError, setCityError] = useState(new FormFieldErrorClass());
  const [stateError, setStateError] = useState(new FormFieldErrorClass());
  const [zipError, setZipError] = useState(new FormFieldErrorClass());
  const [countryCodeError, setCountryCodeError] = useState(
    new FormFieldErrorClass(),
  );

  const formFieldErrors = [
    nameError,
    phoneError,
    address1Error,
    address2Error,
    cityError,
    stateError,
    zipError,
    countryCodeError,
  ];

  useEffect(() => {
    if (location && states && provinces && !isLocationLoaded) {
      setIsLocationLoaded(true);
      setName(location.name);
      setPhone(location.phone);
      setAddress1(location.address1);
      setAddress2(location.address2 || "");
      setCity(location.city);
      setState(location.state);
      setZip(location.zip);
      setCountryCode(location.countryCode);
      if (location.countryCode === "US") {
        setStatesList(states);
        setStateLabel("State");
        setZipLabel("Zip Code");
      } else if (location.countryCode === "CA") {
        setStatesList(provinces);
        setStateLabel("Province");
        setZipLabel("Postal Code");
      }
    }
  }, [location, isLocationLoaded, states, provinces]);

  useEffect(() => {
    if (save) {
      setSave(false);
      if (formFieldErrors.filter((e) => e.hasError).length) {
        return;
      }

      //send data
      handleSave({
        name,
        phone,
        address1,
        address2,
        city,
        state,
        zip,
        countryCode,
      });
    }
  }, [save, formFieldErrors]);

  useEffect(() => {
    if ((isEdit && isLocationLoaded) || !isEdit) {
      switch (countryCode) {
        case "US":
          setStatesList(states);
          setStateLabel("State");
          setZipLabel("Zip Code");
          if (zip) {
            validate.zip(zip); //revalidate zip on country change
          }
          break;
        case "CA":
          setStatesList(provinces);
          setStateLabel("Province");
          setZipLabel("Postal Code");
          if (zip) {
            validate.zip(zip); //revalidate zip on country change
          }
          break;
        default:
          break;
      }
    }
  }, [countryCode]);

  const validate = {
    name: (value) => {
      setNameError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setNameError({
          hasError: true,
          message: "Location Name Required",
        });
        return;
      }
      if (value.length > 100) {
        setNameError({
          hasError: true,
          message: "Location Name Max 100 Characters",
        });
        return;
      }
    },
    phone: (value) => {
      setPhoneError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setPhoneError({
          hasError: true,
          message: "Phone Required",
        });
        return;
      }
      if (!isPhoneNumberValid(value)) {
        setPhoneError({
          hasError: true,
          message: "Phone Number Not Valid",
        });
        return;
      }
    },
    address1: (value) => {
      setAddress1Error({
        hasError: false,
        message: "",
      });
      if (!value) {
        setAddress1Error({
          hasError: true,
          message: "Street Address 1 Required",
        });
        return;
      }
      if (value.length > 100) {
        setAddress1Error({
          hasError: true,
          message: "Street Address 1 Max 100 Charaters",
        });
        return;
      }
    },
    address2: (value) => {
      setAddress2Error({
        hasError: false,
        message: "",
      });
      if (value.length > 100) {
        setAddress2Error({
          hasError: true,
          message: "Street Address 2 Max 100 Charaters",
        });
        return;
      }
    },
    city: (value) => {
      setCityError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setCityError({
          hasError: true,
          message: "City Required",
        });
        return;
      }
      if (value.length > 100) {
        setCityError({
          hasError: true,
          message: "City Max 100 Charaters",
        });
        return;
      }
    },
    state: (value) => {
      setStateError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setStateError({
          hasError: true,
          message: "State Required",
        });
        return;
      }
    },
    zip: (value) => {
      setZipError({
        hasError: false,
        message: "",
      });
      switch (countryCode) {
        case "US":
          if (!value) {
            setZipError({
              hasError: true,
              message: "Zip Code Required",
            });
            return;
          }
          if (!isPostalCode(value, ["US"])) {
            setZipError({
              hasError: true,
              message: "Zip Code Format Not Valid",
            });
            return;
          }
          break;
        case "CA":
          if (!value) {
            setZipError({
              hasError: true,
              message: "Postal Code Required",
            });
            return;
          }
          if (!isPostalCode(value, ["CA"])) {
            setZipError({
              hasError: true,
              message: "Postal Code Format Not Valid",
            });
            return;
          }
          break;
        default:
          break;
      }
    },
    countryCode: (value) => {
      setCountryCodeError({
        hasError: false,
        message: "",
      });
      if (!value) {
        setCountryCodeError({
          hasError: true,
          message: "Country Required",
        });
        return;
      }
    },
  };

  const handlers = {
    nameChange: (e) => {
      const value = e.target.value;
      validate.name(value);
      setName(e.target.value);
    },
    phoneChange: (phoneData) => {
      console.log("phoneData", phoneData);
      validate.phone(phoneData.phone);
      setPhone(phoneData.phone);
    },
    address1Change: (e) => {
      const value = e.target.value;
      validate.address1(value);
      setAddress1(e.target.value);
    },
    address2Change: (e) => {
      const value = e.target.value;
      validate.address2(value);
      setAddress2(e.target.value);
    },
    cityChange: (e) => {
      const value = e.target.value;
      validate.city(value);
      setCity(e.target.value);
    },
    stateChange: (e) => {
      const value = e.target.value;
      validate.state(value);
      setState(e.target.value);
    },
    zipChange: (e) => {
      const value = e.target.value;
      validate.zip(value);
      setZip(e.target.value);
    },
    countryCodeChange: (e) => {
      const value = e.target.value;
      validate.countryCode(value);
      setCountryCode(e.target.value);
      setState("");
    },
    save: () => {
      //validate all
      validate.name(name);
      validate.phone(phone);
      validate.address1(address1);
      validate.address2(address2);
      validate.city(city);
      validate.state(state);
      validate.zip(zip);
      validate.countryCode(countryCode);
      setSave(true);
    },
    cancel: () => {
      handleCancel();
    },
  };

  return (
    <>
      <FormContainer>
        <HeaderNote>
          All fields required (street address 2 is optional)
        </HeaderNote>
        <TextField
          id="location-name"
          label="Location Name"
          variant="outlined"
          value={name}
          onChange={handlers.nameChange}
          helperText={nameError.message}
          error={nameError.hasError}
        />
        <InternationalPhoneInput
          value={phone}
          onChange={handlers.phoneChange}
          helperText={phoneError.message}
          error={phoneError.hasError}
          disabled={loading}
          isMobile={false}
        />
        <TextField
          id="country-code"
          select
          variant="outlined"
          label="Country"
          value={countryCode}
          helperText={countryCodeError.message}
          error={countryCodeError.hasError}
          slotProps={{
            select: {
              native: true,
            },
          }}
          onChange={handlers.countryCodeChange}
        >
          {countries.map((country) => {
            return (
              <option key={country.code} value={country.code}>
                {country.label}
              </option>
            );
          })}
        </TextField>
        <TextField
          id="street-address-1"
          label="Street Address 1"
          variant="outlined"
          value={address1}
          onChange={handlers.address1Change}
          helperText={address1Error.message}
          error={address1Error.hasError}
        />
        <TextField
          id="street-address-2"
          label="Street Address 2 (optional)"
          variant="outlined"
          value={address2}
          onChange={handlers.address2Change}
          helperText={address2Error.message}
          error={address2Error.hasError}
        />
        <TextField
          id="city"
          label="City"
          variant="outlined"
          value={city}
          onChange={handlers.cityChange}
          helperText={cityError.message}
          error={cityError.hasError}
        />
        <TextField
          id="state"
          select
          variant="outlined"
          label={stateLabel}
          value={state}
          helperText={stateError.message}
          error={stateError.hasError}
          slotProps={{
            select: {
              native: true,
            },
          }}
          onChange={handlers.stateChange}
        >
          <option value=""></option>
          {statesList.map((state) => {
            return (
              <option key={state.abbrev} value={state.abbrev}>
                {state.name}
              </option>
            );
          })}
        </TextField>
        <TextField
          id="portal-zip-code"
          label={zipLabel}
          variant="outlined"
          value={zip}
          onChange={handlers.zipChange}
          helperText={zipError.message}
          error={zipError.hasError}
        />
      </FormContainer>
      {error && (
        <FormSubmitError>
          <ErrorIcon fontSize="small" />
          {error}
        </FormSubmitError>
      )}

      <ActionButtonsContainer>
        <Button onClick={handlers.cancel}>Cancel</Button>
        {loading ? (
          <Button disabled variant="contained">
            Saving&#8230;
          </Button>
        ) : (
          <Button onClick={handlers.save} variant="contained">
            Save
          </Button>
        )}
      </ActionButtonsContainer>
    </>
  );
};

export default OrganizationLocationForm;
